import { useEffect, useState } from 'react';
import startCase from 'lodash.startcase';
import { storageItemName } from '../../config';
import * as storage from '../../browserStorage';
import { useAppState } from '../../contexts/AppStateContext';
import { useCart } from "../../contexts/CartContext";

import { useRetailerData } from '../../hooks/dutchie-queries';
import { useCreateCart } from '../../hooks/dutchie-mutations';
import { useLocation } from 'react-router-dom';
import { consoleDebug } from '../../util';

const PricingType = ({ forcedOrderType }) => {
    const { retailerId } = useAppState();
    const location = useLocation();
    const [displayPricingType, setDisplayPricingType] = useState(null);
    const [isMobile, setIsMobile] = useState(false);

    const { cartData, isFetched: isFetchedCartData } = useCart();
    const { data: retailerData, isFetched: isFetchedRetailerData } =
        useRetailerData(retailerId);

    const handleCreateCart = useCreateCart(retailerId);

    // Calculate the effective pricing type (URL param takes precedence)
    useEffect(() => {
        // Get from URL parameter directly (if needed as backup)
        const urlMenuType = new URLSearchParams(location.search).get('menuType');
        
        // Get from forcedOrderType prop (preferred way)
        const propMenuType = forcedOrderType?.menuType;
        
        let effectivePricingType = cartData?.pricingType;
        
        // Override with URL parameter if present
        if (propMenuType || urlMenuType) {
            const menuTypeParam = (propMenuType || urlMenuType).toLowerCase();
            
            if (menuTypeParam === "rec") {
                effectivePricingType = "RECREATIONAL";
            } else if (menuTypeParam === "med") {
                effectivePricingType = "MEDICAL";
            }
        }
        
        consoleDebug('🔄 PricingType Component State:', {
            urlMenuType,
            propMenuType,
            cartPricingType: cartData?.pricingType,
            effectivePricingType
        });
        
        setDisplayPricingType(effectivePricingType);
    }, [cartData?.pricingType, forcedOrderType, location.search]);
    
    // Check screen size on mount and when window resizes
    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        // Initial check
        checkScreenSize();
        
        // Listen for resize events
        window.addEventListener('resize', checkScreenSize);
        
        // Cleanup
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Function to get display text based on menu type and screen size
    const getDisplayText = (menuType) => {
        if (!isMobile) {
            return startCase(menuType.toLowerCase());
        } else {
            // Short versions for mobile
            if (menuType === 'RECREATIONAL') return 'Rec';
            if (menuType === 'MEDICAL') return 'Med';
            return startCase(menuType.toLowerCase());
        }
    };

    // If there's only one menu type or data isn't fetched, don't render anything
    if (!isFetchedRetailerData || retailerData?.menuTypes.length <= 1) {
        return null;
    }

    return (
        <div className='headless-pricingtype-select-wrapper'>
            <select
                name='pricingtype-select'
                id='pricingtype-select'
                className='headless-pricingtype-select'
                aria-label='Select pricing type'
                onChange={(e) => {
                    let newCart = true;

                    if (cartData?.items.length > 0) {
                        newCart = confirm(
                            'WARNING: You are about to create a new cart based on your changes. Your current cart will be cleared. Press OK to continue or Cancel to go back.'
                        );
                    }

                    if (newCart) {
                        storage.removeItem(storageItemName);

                        handleCreateCart.mutate({
                            retailerId: retailerData.id,
                            orderType: 'PICKUP',
                            pricingType: e.target.value,
                        });
                        
                        // Update the URL to include the menu type parameter
                        const newUrl = new URL(window.location.href);
                        if (e.target.value === "RECREATIONAL") {
                            newUrl.searchParams.set('menuType', 'rec');
                        } else if (e.target.value === "MEDICAL") {
                            newUrl.searchParams.set('menuType', 'med');
                        }
                        window.history.replaceState({}, '', newUrl.toString());
                        
                        window.location.reload(false);
                    }
                }}
                value={displayPricingType || cartData?.pricingType}
            >
                {retailerData.menuTypes.map((menuType) => (
                    <option key={menuType} value={menuType}>
                        {getDisplayText(menuType)}
                    </option>
                ))}
            </select>
        </div>
    );
};

export default PricingType;
